import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

// JWT payload interface
interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
}

// Cookie auth is now the default (and only) storage mechanism. Tokens are
// still mirrored into localStorage so any remaining legacy code that reads
// `apollo-token` continues to work until it is fully migrated.

// NOTE: If you are looking for the old `USE_COOKIE_AUTH` flag – it has been
// removed as of July 2025. All reads now prefer cookies and transparently
// fall back to localStorage; all writes update BOTH locations.

// Cookie configuration for secure storage
const COOKIE_OPTIONS = {
  secure: process.env.NODE_ENV === 'production', // Only secure in production
  sameSite: 'strict' as const,
  path: '/',
  expires: 7 // 7 days for access token cookies
};

const REFRESH_COOKIE_OPTIONS = {
  ...COOKIE_OPTIONS,
  expires: 30 // 30 days for refresh token cookies
};

// Storage keys
const ACCESS_TOKEN_COOKIE_KEY = 'access_token';
const REFRESH_TOKEN_COOKIE_KEY = 'refresh_token';

// For legacy localStorage mode we keep the original keys so existing code continues to work
const ACCESS_TOKEN_LS_KEY = 'apollo-token';
const REFRESH_TOKEN_LS_KEY = 'apollo-refresh-token';

/**
 * Check if a JWT token is valid (not expired)
 * @param token - JWT token string
 * @returns true if token exists and is not expired
 */
const isTokenValid = (token: string | null): boolean => {
  if (!token) return false;

  try {
    const decoded = jwtDecode<JWTPayload>(token);

    // No expiration claim = invalid token
    if (!decoded.exp) return false;

    // Check if token is expired (with 60 second buffer to prevent race conditions)
    const expiryTime = decoded.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const bufferTime = 60 * 1000; // 60 seconds buffer

    return expiryTime > now + bufferTime;
  } catch (error) {
    // Invalid JWT format
    console.error('Invalid JWT token format:', error);
    return false;
  }
};

/**
 * Clear only the expired access token (preserves refresh token for silent re-auth).
 * Used by getAccessToken() when it detects an expired JWT.
 */
const clearExpiredAccessToken = (): void => {
  Cookies.remove(ACCESS_TOKEN_COOKIE_KEY, { path: '/' });
  localStorage.removeItem(ACCESS_TOKEN_LS_KEY);
};

/**
 * Clear all authentication data
 */
export const clearAuth = (): void => {
  // Include both frontend (apollo-*) and backend (access_token, refresh_token)
  const keysToRemove = [
    ACCESS_TOKEN_COOKIE_KEY,
    REFRESH_TOKEN_COOKIE_KEY,
    ACCESS_TOKEN_LS_KEY,
    REFRESH_TOKEN_LS_KEY,
    'access_token',
    'refresh_token',
    'permissions',
    'OrganizationPermissions',
    'statistics',
    'user'
  ];

  keysToRemove.forEach(key => {
    Cookies.remove(key, { path: '/' });
    localStorage.removeItem(key);
  });
};

/**
 * Get access token from secure storage
 * Validates token expiration before returning. If expired, clears only the access token
 * (refresh token is preserved for silent re-authentication).
 */
export const getAccessToken = (): string | null => {
  // E2E preview harness — dev builds only.
  // Playwright sets localStorage['E2E_PREVIEW_TOKEN'] before navigation so the
  // router guard (isAuthenticated) AND the Apollo auth link both see a valid
  // Bearer token without a real login flow. Gated on import.meta.env.DEV so
  // production builds dead-code-eliminate this entire branch.
  if (import.meta.env.DEV) {
    const e2ePreviewToken = localStorage.getItem('E2E_PREVIEW_TOKEN');
    if (e2ePreviewToken) return e2ePreviewToken;
  }

  // Check cookie first (preferred)
  const cookieToken = Cookies.get(ACCESS_TOKEN_COOKIE_KEY);

  // Check localStorage (backward compatibility)
  const lsToken = localStorage.getItem(ACCESS_TOKEN_LS_KEY);

  // Validate cookie token if exists
  if (cookieToken) {
    if (isTokenValid(cookieToken)) {
      return cookieToken;
    }
    // Cookie token expired — only clear the access token; preserve the refresh
    // token so bootstrapUser() / the proactive timer can silently re-authenticate.
    console.log('🔒 Cookie token expired - clearing access token (refresh token preserved)');
    clearExpiredAccessToken();
    return null;
  }

  // Validate localStorage token if exists
  if (lsToken) {
    if (isTokenValid(lsToken)) {
      return lsToken;
    }
    // localStorage token expired — only clear the access token; preserve the refresh
    // token so bootstrapUser() / the proactive timer can silently re-authenticate.
    console.log('🔒 localStorage token expired - clearing access token (refresh token preserved)');
    clearExpiredAccessToken();
    return null;
  }

  // No tokens found
  return null;
};

/**
 * Set access token in secure storage
 */
export const setAccessToken = (token: string): void => {
  // Always set cookie **and** legacy LS key for backward compatibility
  Cookies.set(ACCESS_TOKEN_COOKIE_KEY, token, COOKIE_OPTIONS);
  localStorage.setItem(ACCESS_TOKEN_LS_KEY, token);
};

/**
 * Get refresh token from secure storage
 */
export const getRefreshToken = (): string | null => {
  return (
    Cookies.get(REFRESH_TOKEN_COOKIE_KEY) || localStorage.getItem(REFRESH_TOKEN_LS_KEY) || null
  );
};

/**
 * Set refresh token in secure storage
 */
export const setRefreshToken = (token: string): void => {
  Cookies.set(REFRESH_TOKEN_COOKIE_KEY, token, REFRESH_COOKIE_OPTIONS);
  localStorage.setItem(REFRESH_TOKEN_LS_KEY, token);
};

// Generic helpers continue to use **localStorage only**.  Non-sensitive UI
// state and feature flags remain there until a later cleanup phase.
export const getItem = (key: string): string | null => {
  return localStorage.getItem(key);
};

export const setItem = (key: string, value: string): void => {
  localStorage.setItem(key, value);
};

export const removeItem = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Check if user is authenticated (has access token)
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Get current storage mode for debugging
 */
export const getStorageMode = (): 'cookies' | 'cookies+localStorage' => {
  return 'cookies';
};

// Default export object containing all methods
const secureStorage = {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  clearAuth,
  getItem,
  setItem,
  removeItem,
  isAuthenticated,
  getStorageMode
};

export default secureStorage;
